import { Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, Baby, Check, FlaskConical, HeartPulse, Scale, Stethoscope } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionTag from "@/components/SectionTag";
import FaqAccordion from "@/components/FaqAccordion";
import CtaBand from "@/components/CtaBand";
import { BookButton, CallButton, EmergencyNote } from "@/components/CTA";
import { SERVICES, SERVICE_IMAGES, SITE } from "@/data/site";

const ICONS = { Stethoscope, Baby, Activity, Scale, HeartPulse, FlaskConical };

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  const Icon = ICONS[service.icon];

  return (
    <main data-testid={`service-page-${service.slug}`}>
      <Seo title={service.metaTitle} description={service.metaDesc} />

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-14 sm:px-6 md:pt-20 lg:grid-cols-12 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-redlight">
                <Icon className="h-5 w-5 text-brand-red" />
              </span>
              <SectionTag number={service.number} label="Our Services" />
            </div>
            <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
              {service.name}{" "}
              <span className="font-serif italic font-medium text-brand-red">
                in Prescott Valley
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {service.tagline}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <BookButton testid="service-hero-book-online-btn" />
              <CallButton testid="service-hero-call-btn" label="Call for Same-Day Care" />
            </div>
            <EmergencyNote testid="service-hero-emergency-note" className="mt-5 max-w-md" />
          </motion.div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-3 rotate-2 rounded-[2.5rem] bg-brand-redlight" />
              <div className="absolute -inset-3 -rotate-1 rounded-[2.5rem] border border-slate-200" />
              <img
                src={SERVICE_IMAGES[service.slug].src}
                alt={SERVICE_IMAGES[service.slug].alt}
                data-testid="service-hero-image"
                className="relative aspect-[4/3] w-full rounded-[2rem] object-cover shadow-lift"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/70">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionTag number="A" label="The Service" />
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">
                Care built around you
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                {service.intro}
              </p>
              <p className="mt-5 text-sm leading-relaxed text-slate-500">
                Serving {SITE.serviceAreas.join(", ")} — with evening and
                weekend appointments {SITE.hours.toLowerCase()}, by appointment.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-soft">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red">
                  Who It May Be For
                </p>
                <ul className="mt-5 space-y-4">
                  {service.whoFor.map((item, i) => (
                    <li
                      key={i}
                      data-testid={`who-for-item-${i + 1}`}
                      className="flex items-start gap-3"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-redlight">
                        <Check className="h-3.5 w-3.5 text-brand-red" />
                      </span>
                      <span className="text-sm leading-relaxed text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-12">
          <Reveal className="max-w-2xl">
            <SectionTag number="B" label="Common Questions" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">
              {service.name} FAQs
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <FaqAccordion items={service.faqs} testidPrefix="service-faq" />
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Ready to talk about your care?"
        accent="your care?"
        sub={`Book a ${service.name.toLowerCase()} appointment online, or call ${SITE.phone} for same-day availability.`}
        testidPrefix="service-cta"
      />

    </main>
  );
}
